import {
  GridItem,
  Text,
  useViewport,
  Grid,
  Nowrap,
  FooterLayout,
} from '@farmersdog/corgi-x';
import { useLocation } from 'react-router';
import { useMemo } from 'react';

import { NewsletterSection } from './NewsletterSection';
import { Tagline } from './Tagline';
import { SocialLinks } from './SocialLinks';
import { LegalDetails } from './LegalDetails';
import { useSupportLinks } from './hooks/useSupportLinks';
import { SITEMAP_LINKS, CONTACT_LINKS } from './constants';
import { DIY_FAQ } from 'src/pages/DIY/components/FAQ/constants/ids';
import { PATH_DIY } from '@farmersdog/constants/paths';

import { DividerDesktopSvg, DividerMobileSvg } from './assets';
import styles from './FooterRedesign.module.css';

export const FooterRedesign = () => {
  const viewport = useViewport();
  const isMobile = !viewport.lg;
  const isDesktopXL = viewport.xl;
  const location = useLocation();
  const isDIY = location.pathname === PATH_DIY;
  const supportLinks = useSupportLinks();

  const sitemapLinks = useMemo(() => {
    return SITEMAP_LINKS.map(link => ({
      ...link,
      href: link.label === 'FAQs' && isDIY ? `#${DIY_FAQ}` : link.href,
    }));
  }, [isDIY]);

  return (
    <FooterLayout>
      <Grid gap={isDesktopXL ? '4xl' : isMobile ? 'lg' : 'xxl'}>
        <GridItem
          lg={6}
          xl={7}
          justify="space-between"
          direction="column"
          className={styles.leftContent}
        >
          <Text as="h2" className={styles.title}>
            For the love <Nowrap>of dogs.</Nowrap>
          </Text>
          {!isMobile && (
            <>
              <NewsletterSection />
              <Tagline />
            </>
          )}
        </GridItem>
        <GridItem lg={6} xl={5} className={styles.rightContent}>
          <Grid gap="lg">
            <FooterLayout.Column lg={6}>
              <FooterLayout.LinkGroup title="Site Map">
                <FooterLayout.LinksList>
                  {sitemapLinks.map(link => (
                    <FooterLayout.LinkItem
                      key={link.href}
                      to={link.href}
                      target={link.target}
                      rel={link.rel}
                      badge={link.badge}
                    >
                      {link.label}
                    </FooterLayout.LinkItem>
                  ))}
                </FooterLayout.LinksList>
              </FooterLayout.LinkGroup>
            </FooterLayout.Column>
            <FooterLayout.Column lg={6}>
              <FooterLayout.LinkGroup
                title="Get Support"
                badge={!isDIY ? 'Available 24/7' : undefined}
              >
                <FooterLayout.LinksList aria-label="Get Support">
                  {supportLinks.map(link => (
                    <FooterLayout.LinkItem key={link.href} to={link.href}>
                      {link.label}
                    </FooterLayout.LinkItem>
                  ))}
                </FooterLayout.LinksList>
              </FooterLayout.LinkGroup>
              <FooterLayout.LinkGroup title="Connect">
                <FooterLayout.LinksList aria-label="Contact">
                  {CONTACT_LINKS.map(link => (
                    <FooterLayout.LinkItem
                      key={link.href}
                      to={link.href}
                      target={link.target}
                      rel={link.rel}
                    >
                      {link.label}
                    </FooterLayout.LinkItem>
                  ))}
                </FooterLayout.LinksList>
                <SocialLinks />
              </FooterLayout.LinkGroup>
            </FooterLayout.Column>
            {isMobile && (
              <GridItem lg={12}>
                <NewsletterSection />
                <Tagline />
              </GridItem>
            )}
          </Grid>
        </GridItem>
      </Grid>
      <div className={styles.divider}>
        {isMobile ? <DividerMobileSvg /> : <DividerDesktopSvg />}
      </div>
      <LegalDetails />
    </FooterLayout>
  );
};
